import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    UnstyledButton, Popover, Text, Group, Badge, Box, Loader, Alert,
    Indicator, Tooltip, useMantineColorScheme, Paper, ScrollArea,
} from '@mantine/core';
import { IconChecklist, IconChevronRight, IconAlertCircle } from '@tabler/icons-react';
import type { RequestApprovalTypeSummaryItem, RequestApprovalTypePivotListItem } from '../../types/EntityEntitlements';
import { entityService } from '../../services/entityServiceSingleton';
import { extractErrorMessage } from '../../utils/apiUtils';

type NavigationCategory = 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'blacklist' | 'admin';

interface PendingApprovalsHeaderIconProps {
    summary: RequestApprovalTypeSummaryItem[];
    onNavigate: (destination: string, isEntity?: boolean, category?: NavigationCategory) => void;
}

export const PendingApprovalsHeaderIcon: React.FC<PendingApprovalsHeaderIconProps> = ({ summary, onNavigate }) => {
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const [opened, setOpened] = useState(false);

    const [pivotListByTypeId, setPivotListByTypeId] = useState<Record<string, RequestApprovalTypePivotListItem[]>>({});
    const [pivotListLoadingSet, setPivotListLoadingSet] = useState<Set<string>>(() => new Set());
    const [pivotListErrorByTypeId, setPivotListErrorByTypeId] = useState<Record<string, string>>({});
    const inFlightRef = useRef<Set<string>>(new Set());

    const pivotListByTypeIdRef = useRef(pivotListByTypeId);
    pivotListByTypeIdRef.current = pivotListByTypeId;
    const pivotListErrorByTypeIdRef = useRef(pivotListErrorByTypeId);
    pivotListErrorByTypeIdRef.current = pivotListErrorByTypeId;

    const totalPending = summary.reduce((sum, a) => sum + (a.Counts?.Pending ?? 0), 0);

    const loadPivotList = useCallback(async (approvalTypeId: string) => {
        if (pivotListByTypeIdRef.current[approvalTypeId] !== undefined) return;
        if (pivotListErrorByTypeIdRef.current[approvalTypeId] !== undefined) return;
        if (inFlightRef.current.has(approvalTypeId)) return;
        inFlightRef.current.add(approvalTypeId);
        setPivotListLoadingSet(prev => new Set(prev).add(approvalTypeId));
        setPivotListErrorByTypeId(prev => {
            const next = { ...prev };
            delete next[approvalTypeId];
            return next;
        });
        try {
            const list = await entityService.callRequestApprovalTypePivotList(approvalTypeId);
            setPivotListByTypeId(prev => ({ ...prev, [approvalTypeId]: list }));
        } catch (err: any) {
            setPivotListErrorByTypeId(prev => ({ ...prev, [approvalTypeId]: extractErrorMessage(err) || 'Failed to load pivots' }));
        } finally {
            inFlightRef.current.delete(approvalTypeId);
            setPivotListLoadingSet(prev => { const next = new Set(prev); next.delete(approvalTypeId); return next; });
        }
    }, []);

    useEffect(() => {
        if (opened) {
            summary.forEach((a) => loadPivotList(a.Id));
        }
    }, [opened, summary, loadPivotList]);

    const openRequestApprovals = useCallback(() => {
        setOpened(false);
        onNavigate('Requestapproval', true, 'other');
    }, [onNavigate]);

    if (!summary.length || totalPending === 0) return null;

    const accentColor = '#e65100';

    return (
        <Popover
            opened={opened}
            onChange={setOpened}
            position="bottom-end"
            shadow="lg"
            radius="md"
            width={400}
            withArrow
            arrowSize={10}
        >
            <Popover.Target>
                <Tooltip label={`${totalPending} pending approval${totalPending !== 1 ? 's' : ''}`} position="bottom" withArrow>
                    <Indicator
                        label={totalPending > 99 ? '99+' : totalPending}
                        size={18}
                        color="orange"
                        offset={3}
                        processing={totalPending > 0}
                    >
                        <UnstyledButton
                            onClick={() => setOpened((o) => !o)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                width: 36,
                                height: 36,
                                borderRadius: 'var(--mantine-radius-md)',
                                transition: 'background 120ms ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                            aria-label={`Pending approvals: ${totalPending}`}
                        >
                            <IconChecklist size={22} stroke={1.6} />
                        </UnstyledButton>
                    </Indicator>
                </Tooltip>
            </Popover.Target>

            <Popover.Dropdown p={0}>
                <Box px="sm" py="xs" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                    <Group justify="space-between" align="center">
                        <Group gap={8}>
                            <IconChecklist size={16} style={{ color: accentColor }} />
                            <Text fw={700} size="sm">Pending Approvals</Text>
                        </Group>
                        <Badge variant="filled" color="orange" size="sm" radius="sm">
                            {totalPending}
                        </Badge>
                    </Group>
                </Box>

                <ScrollArea.Autosize mah={380} type="hover" scrollbarSize={6}>
                    {summary.map((approval, ai) => {
                        const pivots = pivotListByTypeId[approval.Id];
                        const loadingPivots = pivotListLoadingSet.has(approval.Id);
                        const errorMsg = pivotListErrorByTypeId[approval.Id];
                        return (
                            <Box key={approval.Id}>
                                <Group
                                    gap="xs"
                                    px="sm"
                                    py={6}
                                    wrap="nowrap"
                                    style={{
                                        borderBottom: '1px solid var(--mantine-color-default-border)',
                                        ...(ai > 0 ? { borderTop: '1px solid var(--mantine-color-default-border)' } : {}),
                                        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                    }}
                                >
                                    <IconChecklist size={14} style={{ color: accentColor, flexShrink: 0 }} />
                                    <Text size="xs" fw={600} style={{ flex: 1 }}>
                                        {approval.DisplayName}
                                    </Text>
                                    {approval.Type && (
                                        <Badge variant="outline" size="xs" color="gray" style={{ flexShrink: 0 }}>
                                            {approval.Type}
                                        </Badge>
                                    )}
                                    <Badge variant="light" color="orange" size="xs" style={{ flexShrink: 0 }}>
                                        {approval.Counts?.Pending ?? 0}
                                    </Badge>
                                </Group>

                                {loadingPivots && (
                                    <Group gap="xs" px="sm" py="xs">
                                        <Loader size={12} />
                                        <Text size="xs" c="dimmed">Loading…</Text>
                                    </Group>
                                )}
                                {errorMsg && !loadingPivots && (
                                    <Alert
                                        color="red" m="xs" py="xs"
                                        icon={<IconAlertCircle size={14} />}
                                        withCloseButton
                                        onClose={() => setPivotListErrorByTypeId(prev => { const next = { ...prev }; delete next[approval.Id]; return next; })}
                                    >
                                        <Text size="xs">{errorMsg}</Text>
                                    </Alert>
                                )}
                                {pivots && pivots.length === 0 && !loadingPivots && !errorMsg && (
                                    <Text size="xs" c="dimmed" px="sm" py="xs">No pivots for this approval type.</Text>
                                )}
                                {pivots && pivots.length > 0 && pivots.map((pivot) => (
                                    <UnstyledButton
                                        key={pivot.Id}
                                        onClick={openRequestApprovals}
                                        w="100%"
                                        py={6}
                                        px="sm"
                                        style={{
                                            borderBottom: '1px solid var(--mantine-color-default-border)',
                                            transition: 'background 120ms ease',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = '';
                                        }}
                                    >
                                        <Group justify="space-between" wrap="nowrap" w="100%" gap="sm">
                                            <Box style={{ flex: 1, minWidth: 0 }}>
                                                <Text size="xs" fw={500}>{pivot.DisplayName}</Text>
                                                <Text size="xs" c="dimmed">{pivot.EntityType}</Text>
                                            </Box>
                                            <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                                                {pivot.$Summary?.Counts?.Pending != null && (
                                                    <Badge variant="light" color="orange" size="xs">
                                                        {pivot.$Summary.Counts.Pending}
                                                    </Badge>
                                                )}
                                                <IconChevronRight size={14} style={{ opacity: 0.4 }} />
                                            </Group>
                                        </Group>
                                    </UnstyledButton>
                                ))}
                            </Box>
                        );
                    })}
                </ScrollArea.Autosize>

                <UnstyledButton
                    onClick={openRequestApprovals}
                    w="100%"
                    py="xs"
                    px="sm"
                    style={{
                        borderTop: '1px solid var(--mantine-color-default-border)',
                        textAlign: 'center',
                        transition: 'background 120ms ease',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = '';
                    }}
                >
                    <Text size="xs" fw={600} c="orange">
                        View All Approvals
                    </Text>
                </UnstyledButton>
            </Popover.Dropdown>
        </Popover>
    );
};
